'use client'

import { useEffect, useRef } from 'react'

/**
 * Ambient "circuit network" background — drifting nodes connected by lines
 * when they fall within CONNECT_DIST, with a pointer-pull interaction that
 * eases back to ambient drift. Ported from Chris's personal site's brand
 * motif, recolored to the purple brand (#9370DB) and kept deliberately
 * subtle (low alpha) so it never competes with the calculator UI on top.
 *
 * Rendered on a `fixed inset-0 -z-10` canvas: it paints above the page's
 * solid background but behind all content. `pointer-events-none` keeps the
 * UI fully clickable; the pointer position comes from window listeners.
 * Respects `prefers-reduced-motion` by painting a single static frame.
 */

const NODE_RGB = '147, 112, 219' // #9370DB MediumPurple
const CONNECT_DIST = 130
const POINTER_RADIUS = 170

interface Node {
	x: number
	y: number
	vx: number
	vy: number
	/** Ambient base velocity — current velocity eases back toward this. */
	bvx: number
	bvy: number
}

function makeNodes(w: number, h: number): Node[] {
	// Density ~1 node per 24k px², capped for perf on large displays.
	const count = Math.min(72, Math.max(18, Math.floor((w * h) / 24000)))
	const nodes: Node[] = []
	for (let i = 0; i < count; i++) {
		// Vary drift by index (Math.random is fine in the browser).
		const bvx = (Math.random() - 0.5) * 0.22
		const bvy = (Math.random() - 0.5) * 0.22
		nodes.push({ x: Math.random() * w, y: Math.random() * h, vx: bvx, vy: bvy, bvx, bvy })
	}
	return nodes
}

interface Pointer {
	x: number
	y: number
	active: boolean
}

function step(nodes: Node[], w: number, h: number, pointer: Pointer): void {
	for (const n of nodes) {
		// Ease current velocity back toward ambient drift (this is the friction).
		n.vx += (n.bvx - n.vx) * 0.03
		n.vy += (n.bvy - n.vy) * 0.03
		// Pointer pull — nodes within radius drift toward the cursor.
		if (pointer.active) {
			const dx = pointer.x - n.x
			const dy = pointer.y - n.y
			const d2 = dx * dx + dy * dy
			if (d2 > 1 && d2 < POINTER_RADIUS * POINTER_RADIUS) {
				const d = Math.sqrt(d2)
				const f = (1 - d / POINTER_RADIUS) * 0.18
				n.vx += (dx / d) * f
				n.vy += (dy / d) * f
			}
		}
		n.x += n.vx
		n.y += n.vy
		// Wrap at edges so the field stays populated.
		if (n.x < 0) n.x = w
		else if (n.x > w) n.x = 0
		if (n.y < 0) n.y = h
		else if (n.y > h) n.y = 0
	}
}

function draw(ctx: CanvasRenderingContext2D, nodes: Node[], w: number, h: number): void {
	ctx.clearRect(0, 0, w, h)
	ctx.lineWidth = 1
	for (let i = 0; i < nodes.length; i++) {
		const a = nodes[i]
		if (!a) continue
		for (let j = i + 1; j < nodes.length; j++) {
			const b = nodes[j]
			if (!b) continue
			const dx = a.x - b.x
			const dy = a.y - b.y
			const d2 = dx * dx + dy * dy
			if (d2 >= CONNECT_DIST * CONNECT_DIST) continue
			const alpha = (1 - Math.sqrt(d2) / CONNECT_DIST) * 0.24
			ctx.strokeStyle = `rgba(${NODE_RGB}, ${alpha})`
			ctx.beginPath()
			ctx.moveTo(a.x, a.y)
			ctx.lineTo(b.x, b.y)
			ctx.stroke()
		}
	}
	ctx.fillStyle = `rgba(${NODE_RGB}, 0.68)`
	for (const n of nodes) {
		ctx.beginPath()
		ctx.arc(n.x, n.y, 1.5, 0, Math.PI * 2)
		ctx.fill()
	}
}

export function CircuitNetworkBg() {
	const ref = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = ref.current
		const ctx = canvas?.getContext('2d')
		if (!canvas || !ctx) return

		const dpr = Math.min(window.devicePixelRatio || 1, 2)
		const pointer: Pointer = { x: 0, y: 0, active: false }
		let w = 0
		let h = 0
		let nodes: Node[] = []
		let raf = 0

		const resize = () => {
			w = window.innerWidth
			h = window.innerHeight
			canvas.width = w * dpr
			canvas.height = h * dpr
			canvas.style.width = `${w}px`
			canvas.style.height = `${h}px`
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			nodes = makeNodes(w, h)
		}
		const onMove = (e: MouseEvent) => {
			pointer.x = e.clientX
			pointer.y = e.clientY
			pointer.active = true
		}
		const onTouch = (e: TouchEvent) => {
			const t = e.touches[0]
			if (!t) return
			pointer.x = t.clientX
			pointer.y = t.clientY
			pointer.active = true
		}
		const onLeave = () => {
			pointer.active = false
		}
		const loop = () => {
			step(nodes, w, h, pointer)
			draw(ctx, nodes, w, h)
			raf = requestAnimationFrame(loop)
		}

		resize()
		const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
		if (reduced) {
			draw(ctx, nodes, w, h) // single static frame, no animation
		} else {
			raf = requestAnimationFrame(loop)
		}

		window.addEventListener('resize', resize)
		window.addEventListener('mousemove', onMove)
		window.addEventListener('mouseout', onLeave)
		window.addEventListener('touchmove', onTouch, { passive: true })
		return () => {
			cancelAnimationFrame(raf)
			window.removeEventListener('resize', resize)
			window.removeEventListener('mousemove', onMove)
			window.removeEventListener('mouseout', onLeave)
			window.removeEventListener('touchmove', onTouch)
		}
	}, [])

	return <canvas ref={ref} className="pointer-events-none fixed inset-0 -z-10" />
}
